/**
 * Live tokenizer + linter + completion context for the SQL dialect described
 * in docs/sql-dialect.md. Runs on every keystroke, so it is a single regex
 * scan and a few passes over the token list — no parser. The rules are the
 * CONFIRMED failures from that document; each one names the fix.
 *
 * Pure functions, no DOM: the editor renders what these return.
 */

export type TokenType =
  | 'keyword'
  | 'function'
  | 'field' // $Name
  | 'ident' // bare word — a stream after FROM, an alias after AS, or a mistake
  | 'string'
  | 'number'
  | 'op'
  | 'punct'
  | 'comment'
  | 'ws'
  | 'bad'

export interface Token {
  type: TokenType
  text: string
  /** Offset of the first character in the source. */
  start: number
  /** Offset one past the last character. */
  end: number
}

export type Severity = 'error' | 'warn' | 'hint'

export interface Diagnostic {
  severity: Severity
  message: string
  start: number
  end: number
}

export const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'GROUP', 'BY', 'ORDER', 'HAVING', 'LIMIT', 'AS',
  'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS', 'NULL', 'ASC', 'DESC',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'DISTINCT', 'WITH', 'UNION', 'ALL',
]
export const SQL_FUNCTIONS = [
  'COUNT', 'SUM', 'MIN', 'MAX', 'AVG', 'SUBSTR', 'CAST', 'REGEXP_EXTRACT', 'LOWER', 'UPPER', 'LENGTH',
]
const KEYWORDS = new Set(SQL_KEYWORDS)
const FUNCTIONS = new Set(SQL_FUNCTIONS)
/** Known-missing in the dialect (CONFIRMED failures). */
const BANNED_FUNCTIONS: Record<string, string> = {
  DATE: 'DATE() does not exist — bucket with SUBSTR($SystemTstamp, 1, 10)',
  NOW: 'NOW() is not available',
}

const TOKEN_RE =
  /(\s+)|(--[^\n]*)|('(?:[^'\\]|\\.)*'?)|("[^"]*"?|`[^`]*`?|\[[^\]]*\]?)|(\$[A-Za-z_][\w]*)|(\d+[smhd]\b|\d+(?:\.\d+)?)|([A-Za-z_][\w]*(?:-[A-Za-z_][\w]*)*)|(>=|<=|<>|!=|[=<>+\-/*%])|([(),;.])|([≥≤≠])|(.)/gy

export function tokenize(src: string): Token[] {
  const out: Token[] = []
  TOKEN_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = TOKEN_RE.exec(src)) !== null) {
    const start = m.index
    const text = m[0]
    if (text === '') break
    const end = start + text.length
    let type: TokenType
    if (m[1]) type = 'ws'
    else if (m[2]) type = 'comment'
    else if (m[3]) type = 'string'
    else if (m[4]) type = 'bad' // quoted identifier — never valid here
    else if (m[5]) type = 'field'
    else if (m[6]) type = 'number'
    else if (m[7]) {
      const up = text.toUpperCase()
      type = KEYWORDS.has(up) ? 'keyword' : FUNCTIONS.has(up) || up in BANNED_FUNCTIONS ? 'function' : 'ident'
    } else if (m[8]) type = 'op'
    else if (m[9]) type = 'punct'
    else if (m[10]) type = 'bad' // unicode comparison glyph
    else type = 'bad'
    out.push({ type, text, start, end })
    if (TOKEN_RE.lastIndex === start) TOKEN_RE.lastIndex++
  }
  return out
}

/** Stream name -> its `$`-prefixed field list; the vfields vocabulary. */
export type Vocabulary = Readonly<Record<string, readonly string[]>>

const sig = (tokens: Token[]) => tokens.filter((t) => t.type !== 'ws' && t.type !== 'comment')

/** The stream named after FROM, if there is exactly one bare identifier there. */
export function streamOf(tokens: Token[]): Token | null {
  const s = sig(tokens)
  for (let i = 0; i < s.length - 1; i++) {
    if (s[i]!.type === 'keyword' && s[i]!.text.toUpperCase() === 'FROM') {
      const next = s[i + 1]!
      return next.type === 'ident' ? next : null
    }
  }
  return null
}

/** Aliases declared with `AS name` / `AS $name` — legal bare in GROUP/ORDER BY. */
function aliases(s: Token[]): Set<string> {
  const out = new Set<string>()
  for (let i = 0; i < s.length - 1; i++) {
    if (s[i]!.type === 'keyword' && s[i]!.text.toUpperCase() === 'AS') {
      const n = s[i + 1]!
      if (n.type === 'ident' || n.type === 'field') out.add(n.text.replace(/^\$/, '').toLowerCase())
    }
  }
  return out
}

export function lint(src: string, vocabulary?: Vocabulary | null): Diagnostic[] {
  const tokens = tokenize(src)
  const s = sig(tokens)
  const out: Diagnostic[] = []
  const push = (severity: Severity, t: Token, message: string) =>
    out.push({ severity, message, start: t.start, end: t.end })
  const up = (t: Token | undefined) => t?.text.toUpperCase() ?? ''
  const isKw = (t: Token | undefined, k: string) => !!t && t.type === 'keyword' && up(t) === k

  const streamTok = streamOf(tokens)
  const streamName = streamTok?.text ?? null
  const knownStreams = vocabulary ? Object.keys(vocabulary) : null
  const streamFields =
    vocabulary && streamName
      ? (Object.entries(vocabulary).find(([k]) => k.toLowerCase() === streamName.toLowerCase())?.[1] ?? null)
      : null
  const streamFieldSet = streamFields ? new Set(streamFields.map((f) => f.toLowerCase())) : null
  const als = aliases(s)

  for (let i = 0; i < s.length; i++) {
    const t = s[i]!
    const prev = s[i - 1]
    const next = s[i + 1]

    if (t.type === 'bad') {
      if (/^[≥≤≠]$/.test(t.text)) {
        const ascii = t.text === '≥' ? '>=' : t.text === '≤' ? '<=' : '!='
        push('error', t, `'${t.text}' is a unicode glyph, not an operator — retype it as ${ascii}`)
      } else if (isKw(prev, 'FROM')) {
        push('error', t, 'stream names are bare — no quotes, brackets or backticks')
      } else {
        push('error', t, `unexpected ${t.text}`)
      }
      continue
    }

    if (t.type === 'function') {
      const banned = BANNED_FUNCTIONS[up(t)]
      if (banned) {
        push('error', t, banned)
        continue
      }
      if (up(t) === 'COUNT' && next?.text === '(' && s[i + 2]?.text === ')') {
        push('error', t, 'COUNT() takes an argument — COUNT(*) or COUNT($field)')
      }
      continue
    }

    if (isKw(t, 'FROM')) {
      if (next?.text === '(') push('error', next, 'subqueries are not supported — flatten to one SELECT')
      if (next?.type === 'ident' && s[i + 2]?.text === ',') {
        push('error', s[i + 2]!, 'one stream per query — FROM A, B is not supported; run one query per stream')
      }
      if (next?.type === 'field') push('error', next, `stream names take no $ — FROM ${next.text.slice(1)}`)
      continue
    }

    if (t.type === 'ident') {
      // Legal bare identifiers: the stream after FROM, an alias after AS, an
      // alias reused in GROUP/ORDER BY, and the CASE ... END keywords already
      // classified as keywords. Everything else needed a $.
      if (isKw(prev, 'FROM')) {
        if (knownStreams && !knownStreams.some((k) => k.toLowerCase() === t.text.toLowerCase())) {
          push('error', t, `unknown stream ${t.text}`)
        }
        continue
      }
      if (isKw(prev, 'AS')) continue
      if (als.has(t.text.toLowerCase())) continue
      // Right of a comparison it is a VALUE someone forgot to quote — the
      // engine reads it as a column and fails with UNRESOLVED_COLUMN.
      if (prev?.type === 'op' || isKw(prev, 'LIKE') || isKw(prev, 'IN') || isKw(prev, 'THEN') || isKw(prev, 'ELSE')) {
        push('error', t, `string values are quoted — '${t.text}'`)
        continue
      }
      push('error', t, `field names take a $ — $${t.text}`)
      continue
    }

    if (t.type === 'field') {
      if (isKw(prev, 'AS')) continue
      if (als.has(t.text.slice(1).toLowerCase())) continue
      if (up(t) === '$DURATION') {
        // Pseudo-field: `WHERE $Duration = 1h` bounds the scan to the last
        // <n><unit>, bare literal (live-verified 2026-08-26). Not in vfields.
        const v = s[i + 2]
        if (v?.type === 'string' && /^'\d+[smhd]'$/i.test(v.text)) {
          push('error', v, '$Duration is bare, not quoted — $Duration = 1h')
        } else if (next?.text !== '=' || !v || !/^\d+[smhd]$/i.test(v.text)) {
          push('error', v ?? next ?? t, '$Duration takes a bare <n><m|h|d> — $Duration = 1h')
        }
        continue
      }
      if (streamFieldSet && !streamFieldSet.has(t.text.toLowerCase())) {
        push('warn', t, `${t.text} is not a field of ${streamName}`)
      }
      if (up(t) === '$CNAMTIME' && next?.type === 'op' && s[i + 2]?.type === 'string') {
        // Accepted: epoch ms, or the full 'YYYY-MM-DD HH:MM:SS'. A date-only
        // string is the CONFIRMED failure (docs/sql-dialect.md §2).
        const v = s[i + 2]!
        if (!/^'\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}'$/.test(v.text)) {
          push('error', v, "$CNAMTime wants a full timestamp 'YYYY-MM-DD HH:MM:SS' (or epoch ms) — a date alone fails")
        }
      }
      if (up(t) === '$SYSTEMTSTAMP' && next?.type === 'op' && s[i + 2]?.type === 'string') {
        push(
          'hint',
          t,
          '$SystemTstamp holds mixed formats on some sources (NIX, CISCO-ASA, CHECKPOINT) — non-ISO values sort after digits; guard with SUBSTR($SystemTstamp, 1, 2) = \'20\'',
        )
      }
      continue
    }

    if (t.type === 'string' && !t.text.endsWith("'")) push('error', t, 'unterminated string')
    if (t.type === 'string' && t.text.length >= 2 && t.text.endsWith("'") && t.text.length === 1) push('error', t, 'unterminated string')
  }

  // Structural: SELECT … FROM stream is the minimum.
  const first = s[0]
  if (first && isKw(first, 'SELECT') && !s.some((t) => isKw(t, 'FROM'))) {
    const last = s[s.length - 1]!
    push('error', last, 'missing FROM <stream>')
  }
  return out.sort((a, b) => a.start - b.start)
}

/* --------------------------------------------------------------- completion */

export type CompletionKind = 'stream' | 'field' | 'keyword' | 'function'

export interface Completion {
  label: string
  kind: CompletionKind
  /** Text to insert in place of the prefix. */
  insert: string
}

export interface CompletionContext {
  /** Replace [from, to) in the source with the chosen completion. */
  from: number
  to: number
  prefix: string
  items: Completion[]
}

/**
 * What can be typed at `caret`. Fields after `$`, streams after FROM, else
 * keywords/functions once two letters are down. Fields come from the stream
 * named in FROM when it is known, otherwise from every stream.
 */
export function complete(src: string, caret: number, vocabulary?: Vocabulary | null): CompletionContext | null {
  const tokens = tokenize(src)
  const WORDS = new Set(['ident', 'keyword', 'function', 'field', 'bad', 'string', 'number', 'comment'])
  const at = tokens.find((t) => t.start < caret && caret <= t.end && WORDS.has(t.type))
  const before = sig(tokens.filter((t) => t.end <= (at ? at.start : caret)))
  const prev = before[before.length - 1]
  const prefix = at ? src.slice(at.start, caret) : ''
  const from = at ? at.start : caret
  const to = at ? at.end : caret
  const isKw = (t: Token | undefined, k: string) => !!t && t.type === 'keyword' && t.text.toUpperCase() === k
  const has = (label: string) => label.toLowerCase().startsWith(prefix.toLowerCase())
  const cap = (xs: Completion[]) => xs.slice(0, 40)

  if (at?.type === 'string' || at?.type === 'comment' || at?.type === 'number') return null

  // Streams: after FROM, or while typing the word after it.
  if (isKw(prev, 'FROM') && (!at || at.type === 'ident' || at.type === 'field')) {
    if (!vocabulary) return null
    const items = Object.keys(vocabulary)
      .filter((k) => has(k) || has(`$${k}`))
      .map((k) => ({ label: k, kind: 'stream' as const, insert: k }))
    return items.length ? { from, to, prefix, items: cap(items) } : null
  }

  // Fields: a `$` is down.
  if (at?.type === 'field' || prefix === '$') {
    if (!vocabulary) return null
    const streamTok = streamOf(tokens)
    const entry = streamTok
      ? Object.entries(vocabulary).find(([k]) => k.toLowerCase() === streamTok.text.toLowerCase())
      : undefined
    const pool = entry ? entry[1] : [...new Set(Object.values(vocabulary).flat())]
    const items = [...pool, '$Duration']
      .filter(has)
      .sort((a, b) => a.localeCompare(b))
      .map((f) => ({ label: f, kind: 'field' as const, insert: f }))
    return items.length ? { from, to, prefix, items: cap(items) } : null
  }

  // `$Duration = ` → the presets, bare.
  const p2 = before[before.length - 2]
  if (prev?.text === '=' && p2?.type === 'field' && p2.text.toUpperCase() === '$DURATION') {
    const items = ['15m', '1h', '6h', '24h', '7d', '30d'].filter(has).map((d) => ({ label: d, kind: 'keyword' as const, insert: d }))
    return items.length ? { from, to, prefix, items } : null
  }

  // Keywords / functions after two letters, so a fresh line stays quiet.
  if (at && (at.type === 'ident' || at.type === 'keyword' || at.type === 'function') && prefix.length >= 2) {
    const items: Completion[] = [
      ...SQL_KEYWORDS.filter(has).map((k) => ({ label: k, kind: 'keyword' as const, insert: k })),
      ...SQL_FUNCTIONS.filter(has).map((f) => ({ label: f, kind: 'function' as const, insert: `${f}(` })),
    ]
    return items.length ? { from, to, prefix, items: cap(items) } : null
  }
  return null
}
